import React, { useState, useEffect } from 'react';
import { X, Loader2, Flame } from 'lucide-react';
import { roaryApi } from '../../api/modules';
import { ApiError } from '../../api/core';
import { cn } from '../../utils/cn';

function describeApiError(err: unknown, fallback: string): string {
  if (err instanceof ApiError) {
    const backendMessage =
      (err.data as { error?: string; message?: string } | undefined)?.error ??
      (err.data as { error?: string; message?: string } | undefined)?.message ??
      err.message;
    return `${fallback} (HTTP ${err.status}). ${backendMessage || 'Please try again or contact support.'}`;
  }
  if (err instanceof Error) return `${fallback}. ${err.message}`;
  return `${fallback}. Please try again or contact support.`;
}

function logApiError(tag: string, err: unknown): void {
  console.group(`[${tag}] request failed`);
  if (err instanceof ApiError) {
    console.error('status:', err.status);
    console.error('data:', err.data);
    console.error('message:', err.message);
  } else {
    console.error(err);
  }
  console.groupEnd();
}

interface MarshmallowPack {
  id: string;
  name: string;
  description: string;
  marshmallows: number;
  price: number;
  priceFormatted: string;
  highlighted: boolean;
}

function defaultSuccessUrl(): string {
  return `${window.location.origin}/dashboard?marshmallow_purchase=success`;
}

function defaultCancelUrl(): string {
  return `${window.location.origin}/dashboard`;
}

interface MarshmallowPacksModalProps {
  isOpen: boolean;
  onClose: () => void;
  successUrl?: string;
  cancelUrl?: string;
}

export const MarshmallowPacksModal: React.FC<MarshmallowPacksModalProps> = ({
  isOpen,
  onClose,
  successUrl,
  cancelUrl,
}) => {
  const [packs, setPacks] = useState<MarshmallowPack[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [buyingPackId, setBuyingPackId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      loadPacks();
    }
  }, [isOpen]);

  const loadPacks = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await roaryApi.getMarshmallowPacks();
      setPacks(response.packs);
    } catch (err) {
      logApiError('Marshmallow packs load', err);
      setError(describeApiError(err, 'Failed to load packs'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleBuy = async (packId: string) => {
    try {
      setBuyingPackId(packId);
      setError(null);
      const { checkoutUrl } = await roaryApi.createMarshmallowCheckout(
        packId,
        successUrl || defaultSuccessUrl(),
        cancelUrl || defaultCancelUrl()
      );
      if (!checkoutUrl) {
        throw new Error('Checkout session did not return a redirect URL.');
      }
      window.location.href = checkoutUrl;
    } catch (err) {
      logApiError('Marshmallow checkout', err);
      setError(describeApiError(err, 'Checkout failed'));
      setBuyingPackId(null);
    }
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black/60 flex items-center justify-center z-[80] p-4 animate-fade-in"
      onClick={handleBackdropClick}
    >
      <div
        className="bg-slate-900 border border-slate-700 rounded-2xl max-w-2xl w-full overflow-hidden animate-slide-up"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-slate-800">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-r from-orange-500 to-amber-500 flex items-center justify-center">
              <Flame size={20} className="text-white" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-white">Get More Marshmallows</h2>
              <p className="text-sm text-slate-400">Top up your balance to keep building</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-slate-400 hover:text-white rounded-lg hover:bg-slate-800 transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {error && (
            <div className="mb-4 p-3 bg-red-950/30 border border-red-900/50 rounded-lg">
              <p className="text-red-300 text-sm">{error}</p>
            </div>
          )}

          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {packs.map(pack => (
                <div
                  key={pack.id}
                  className={cn(
                    'relative rounded-xl border p-5 flex flex-col transition-all',
                    pack.highlighted
                      ? 'border-orange-500 bg-orange-500/10 shadow-lg shadow-orange-500/10'
                      : 'border-slate-700 bg-slate-800/50 hover:border-slate-600'
                  )}
                >
                  {pack.highlighted && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                      <span className="bg-orange-500 text-white text-xs font-bold px-3 py-1 rounded-full whitespace-nowrap">
                        Most Popular
                      </span>
                    </div>
                  )}

                  <div className="text-center mb-4 mt-1">
                    <h3 className="text-white font-semibold text-base">{pack.name}</h3>
                    <p className="text-slate-400 text-xs mt-1">{pack.description}</p>
                  </div>

                  <div className="text-center mb-4">
                    <div className="flex items-baseline justify-center gap-1">
                      <span className="text-3xl font-bold text-white">{pack.marshmallows}</span>
                      <span className="text-sm text-slate-400">marshmallows</span>
                    </div>
                    <p className="text-orange-400 font-semibold text-lg mt-1">
                      {pack.priceFormatted}
                    </p>
                  </div>

                  <button
                    onClick={() => handleBuy(pack.id)}
                    disabled={buyingPackId !== null}
                    className={cn(
                      'mt-auto w-full py-2.5 px-4 rounded-lg font-medium text-sm transition-all',
                      'disabled:opacity-50 disabled:cursor-not-allowed',
                      pack.highlighted
                        ? 'bg-orange-500 hover:bg-orange-600 text-white'
                        : 'bg-slate-700 hover:bg-slate-600 text-white'
                    )}
                  >
                    {buyingPackId === pack.id ? (
                      <span className="flex items-center justify-center gap-2">
                        <Loader2 size={14} className="animate-spin" />
                        Loading...
                      </span>
                    ) : (
                      'Buy Now'
                    )}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-800">
          <p className="text-xs text-slate-500 text-center">
            One-time purchase. Marshmallows are added to your balance immediately after payment.
          </p>
        </div>
      </div>
    </div>
  );
};
